import * as React from "react"
import { useLocation } from "@gatsbyjs/reach-router"
import { useDialKit } from "dialkit"

const CURSOR_ENDPOINT = "/api/presence"
const LOCAL_FUNCTIONS_HOST = "localhost:8788"
const SEND_INTERVAL_MS = 40
const MAX_RECONNECT_DELAY_MS = 30000
const IDLE_CHECK_INTERVAL_MS = 250
const IDLE_CURSOR_TIMEOUT_MS = 60000
const SETTINGS_STORAGE_KEY = "presence-cursor-settings"
const DEFAULT_CURSOR_SETTINGS = {
  cursorsEnabled: true,
  idleFadeSeconds: 0.75,
}
const MIN_IDLE_FADE_SECONDS = 0.25
const MAX_IDLE_FADE_SECONDS = 5
const IDLE_FADE_STEP_SECONDS = 0.25

function isLocalGatsbyHost() {
  return (
    process.env.NODE_ENV === "development" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname) &&
    window.location.port !== "8788"
  )
}

function makeCursorUrl(pathname) {
  const isLocalProxyTarget = isLocalGatsbyHost()
  const protocol = isLocalProxyTarget
    ? "ws:"
    : window.location.protocol === "https:" ? "wss:" : "ws:"
  const baseUrl = isLocalProxyTarget ? `http://${LOCAL_FUNCTIONS_HOST}` : window.location.href
  const url = new URL(CURSOR_ENDPOINT, baseUrl)
  url.protocol = protocol
  url.searchParams.set("room", pathname || "/")
  return url.toString()
}

function isPresenceCursorDisabledPath(pathname = "") {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/"
  return (
    normalizedPath === "/watch-together" ||
    normalizedPath.startsWith("/watch-together/") ||
    normalizedPath === "/listen-together" ||
    normalizedPath.startsWith("/listen-together/") ||
    normalizedPath === "/repl" ||
    normalizedPath.startsWith("/repl/")
  )
}

function clampRatio(value) {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value))
}

function toFiniteNumber(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback
}

function getPeerUpdatedAt(peer, fallback = 0) {
  return toFiniteNumber(peer?.updatedAt, fallback)
}

function isFreshPeer(peer, now = Date.now()) {
  return now - getPeerUpdatedAt(peer, peer?.receivedAt || 0) <= IDLE_CURSOR_TIMEOUT_MS
}

function clampIdleFadeSeconds(value) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return DEFAULT_CURSOR_SETTINGS.idleFadeSeconds

  return Math.max(
    MIN_IDLE_FADE_SECONDS,
    Math.min(MAX_IDLE_FADE_SECONDS, numericValue)
  )
}

function readStoredCursorSettings() {
  if (typeof window === "undefined") return DEFAULT_CURSOR_SETTINGS

  try {
    const storedValue = window.localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (!storedValue) return DEFAULT_CURSOR_SETTINGS

    const parsedValue = JSON.parse(storedValue)
    return {
      cursorsEnabled:
        typeof parsedValue.cursorsEnabled === "boolean"
          ? parsedValue.cursorsEnabled
          : DEFAULT_CURSOR_SETTINGS.cursorsEnabled,
      idleFadeSeconds: clampIdleFadeSeconds(parsedValue.idleFadeSeconds),
    }
  } catch (_) {
    return DEFAULT_CURSOR_SETTINGS
  }
}

const PresenceCursorsLayer = ({ pathname }) => {
  const initialCursorSettings = React.useMemo(readStoredCursorSettings, [])
  const cursorSettings = useDialKit("Presence Cursors", {
    cursorsEnabled: initialCursorSettings.cursorsEnabled,
    idleFadeSeconds: [
      initialCursorSettings.idleFadeSeconds,
      MIN_IDLE_FADE_SECONDS,
      MAX_IDLE_FADE_SECONDS,
      IDLE_FADE_STEP_SECONDS,
    ],
  })
  const socketRef = React.useRef(null)
  const reconnectTimerRef = React.useRef(null)
  const reconnectAttemptRef = React.useRef(0)
  const latestCursorRef = React.useRef(null)
  const lastSentAtRef = React.useRef(0)
  const frameRef = React.useRef(null)
  const [clientId, setClientId] = React.useState(null)
  const [peers, setPeers] = React.useState({})
  const [scrollPosition, setScrollPosition] = React.useState({ x: 0, y: 0 })
  const [now, setNow] = React.useState(Date.now())

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        SETTINGS_STORAGE_KEY,
        JSON.stringify({
          cursorsEnabled: cursorSettings.cursorsEnabled,
          idleFadeSeconds: clampIdleFadeSeconds(cursorSettings.idleFadeSeconds),
        })
      )
    } catch (_) {
      // localStorage can be unavailable in private or restricted contexts.
    }
  }, [cursorSettings.cursorsEnabled, cursorSettings.idleFadeSeconds])

  React.useEffect(() => {
    if (typeof window === "undefined" || !("WebSocket" in window)) return undefined

    let isDisposed = false

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    const scheduleReconnect = () => {
      if (isDisposed) return
      clearReconnectTimer()
      const delay = Math.min(
        MAX_RECONNECT_DELAY_MS,
        1000 * 2 ** reconnectAttemptRef.current
      )
      reconnectAttemptRef.current += 1
      reconnectTimerRef.current = window.setTimeout(connect, delay)
    }

    const connect = () => {
      if (isDisposed || document.visibilityState === "hidden") return

      const socket = new WebSocket(makeCursorUrl(pathname))
      socketRef.current = socket

      socket.addEventListener("open", () => {
        reconnectAttemptRef.current = 0
        const latestCursor = latestCursorRef.current
        if (latestCursor) {
          socket.send(JSON.stringify({ type: "cursor", ...latestCursor }))
        }
      })

      socket.addEventListener("message", event => {
        try {
          const message = JSON.parse(event.data)
          const receivedAt = Date.now()

          if (message.type === "init") {
            setClientId(message.id)
            setPeers(
              Object.fromEntries(
                (message.peers || [])
                  .filter(peer => peer?.id && isFreshPeer(peer, receivedAt))
                  .map(peer => [peer.id, { ...peer, receivedAt }])
              )
            )
            return
          }

          if (message.type === "cursor" && message.peer?.id) {
            setPeers(current => ({
              ...current,
              [message.peer.id]: { ...message.peer, receivedAt },
            }))
            return
          }

          if (message.type === "leave" && message.id) {
            setPeers(current => {
              const next = { ...current }
              delete next[message.id]
              return next
            })
          }
        } catch (_) {
          // Ignore malformed presence messages.
        }
      })

      socket.addEventListener("close", () => {
        if (socketRef.current === socket) socketRef.current = null
        if (!isDisposed) scheduleReconnect()
      })

      socket.addEventListener("error", () => {
        socket.close()
      })
    }

    const disconnect = () => {
      clearReconnectTimer()
      if (socketRef.current) {
        socketRef.current.close(1000, "page hidden")
        socketRef.current = null
      }
      setPeers({})
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnect()
      } else {
        reconnectAttemptRef.current = 0
        connect()
      }
    }

    if (!cursorSettings.cursorsEnabled) {
      disconnect()
      return () => {
        isDisposed = true
        disconnect()
      }
    }

    connect()
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      isDisposed = true
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      disconnect()
    }
  }, [cursorSettings.cursorsEnabled, pathname])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined
    if (!cursorSettings.cursorsEnabled) return undefined

    const sendCursor = () => {
      frameRef.current = null

      const socket = socketRef.current
      const latestCursor = latestCursorRef.current
      if (!latestCursor || socket?.readyState !== WebSocket.OPEN) return

      const now = Date.now()
      if (now - lastSentAtRef.current < SEND_INTERVAL_MS) return
      lastSentAtRef.current = now
      socket.send(JSON.stringify({ type: "cursor", ...latestCursor }))
    }

    const handlePointerMove = event => {
      latestCursorRef.current = {
        x: clampRatio(event.clientX / window.innerWidth),
        y: clampRatio(event.clientY / window.innerHeight),
        pageX: event.pageX,
        pageY: event.pageY,
      }

      if (frameRef.current === null) {
        frameRef.current = window.requestAnimationFrame(sendCursor)
      }
    }

    window.addEventListener("pointermove", handlePointerMove, { passive: true })

    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
    }
  }, [cursorSettings.cursorsEnabled])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined

    const updateScrollPosition = () => {
      setScrollPosition({
        x: window.scrollX || 0,
        y: window.scrollY || 0,
      })
    }

    updateScrollPosition()
    window.addEventListener("scroll", updateScrollPosition, { passive: true })
    window.addEventListener("resize", updateScrollPosition)

    return () => {
      window.removeEventListener("scroll", updateScrollPosition)
      window.removeEventListener("resize", updateScrollPosition)
    }
  }, [])

  React.useEffect(() => {
    if (!cursorSettings.cursorsEnabled) return undefined

    const intervalId = window.setInterval(() => {
      const timestamp = Date.now()
      setNow(timestamp)
      setPeers(current => {
        let hasExpiredPeer = false
        const next = {}

        Object.entries(current).forEach(([id, peer]) => {
          if (isFreshPeer(peer, timestamp)) {
            next[id] = peer
          } else {
            hasExpiredPeer = true
          }
        })

        return hasExpiredPeer ? next : current
      })
    }, IDLE_CHECK_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [cursorSettings.cursorsEnabled])

  if (!cursorSettings.cursorsEnabled) return null

  const visiblePeers = Object.values(peers).filter(
    peer => peer.id !== clientId && isFreshPeer(peer, now)
  )

  if (visiblePeers.length === 0) return null

  const viewportWidth = typeof window === "undefined" ? 0 : window.innerWidth
  const viewportHeight = typeof window === "undefined" ? 0 : window.innerHeight

  return (
    <div className="presence-cursors" aria-hidden="true" data-glass-exclude>
      {visiblePeers.map(peer => (
        <div
          key={peer.id}
          className={`presence-cursor${
            now - (peer.receivedAt || 0) > cursorSettings.idleFadeSeconds * 1000
              ? " is-idle"
              : ""
          }`}
          style={{
            "--presence-x": `${clampRatio(peer.x) * 100}vw`,
            "--presence-y": `${clampRatio(peer.y) * 100}vh`,
            "--presence-page-x": `${toFiniteNumber(peer.pageX, clampRatio(peer.x) * viewportWidth) - scrollPosition.x}px`,
            "--presence-page-y": `${toFiniteNumber(peer.pageY, clampRatio(peer.y) * viewportHeight) - scrollPosition.y}px`,
            "--presence-color": peer.color || "#ffffff",
          }}
        >
          <svg
            className="presence-cursor__pointer"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
          >
            <path d="M4 3.5 19.5 15l-7 .9-3.6 5.9L4 3.5Z" />
          </svg>
          <span className="presence-cursor__label">{peer.name || "visitor"}</span>
        </div>
      ))}
    </div>
  )
}

const PresenceCursors = () => {
  const location = useLocation()

  if (isPresenceCursorDisabledPath(location.pathname)) return null

  return <PresenceCursorsLayer pathname={location.pathname} />
}

export default PresenceCursors
